import { memo, useEffect, useRef, useCallback } from 'react'
import { useMap } from 'react-leaflet'
import { createLogger } from '../../utils/logger'
import { toast } from '../../services/toastService'

const logger = createLogger('MapDragHandler')

/**
 * MapDragHandler - Handles map drag events to load entities at new center
 * Implements request cancellation to prevent race conditions on rapid map movements
 * 
 * @param {boolean} enabled - Enable/disable drag-to-load functionality
 * @param {Function} fetchNearbyEntities - Function to fetch entities at location (accepts signal parameter)
 * @param {Function} reverseGeocode - Function to get address from coordinates
 * @param {Function} setUserLocation - Update user location state
 * @param {Function} setSearchLocationName - Update search location name
 * @param {Function} setSearchLocation - Update search location input
 * @param {Function} saveLocation - Save location to localStorage
 * @param {Object} t - Translation function
 */
const MapDragHandler = memo(function MapDragHandler({
  enabled = true,
  fetchNearbyEntities,
  reverseGeocode,
  setUserLocation,
  setSearchLocationName,
  setSearchLocation,
  saveLocation,
  t
}) {
  const map = useMap()
  const debounceTimerRef = useRef(null)
  const lastFetchLocationRef = useRef(null)
  const isLoadingRef = useRef(false)
  const isDraggingRef = useRef(false)
  const lastFetchTimeRef = useRef(0)
  const programmaticMoveRef = useRef(false)
  const abortControllerRef = useRef(null)
  const requestIdRef = useRef(0)
  
  // Add custom property to map instance with timestamp to skip bounds adjustment
  useEffect(() => {
    if (map) {
      map._skipFitBoundsUntil = 0
    }
  }, [map])

  const handleMapMove = useCallback(async () => {
    if (!enabled || !map || isLoadingRef.current || !isDraggingRef.current) return

    // Cooldown: Don't fetch if we just fetched within last 3 seconds
    const now = Date.now()
    if (now - lastFetchTimeRef.current < 3000) {
      return
    }

    const center = map.getCenter()
    const newLocation = {
      latitude: center.lat,
      longitude: center.lng
    }

    // Check if we've moved significantly (at least 100m)
    if (lastFetchLocationRef.current) {
      const distance = map.distance(
        [lastFetchLocationRef.current.latitude, lastFetchLocationRef.current.longitude],
        [newLocation.latitude, newLocation.longitude]
      )
      
      if (distance < 100) return
    }

    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController()
    const currentRequestId = ++requestIdRef.current

    isLoadingRef.current = true
    lastFetchLocationRef.current = newLocation
    lastFetchTimeRef.current = now

    logger.info('Map dragged to new location', { 
      lat: newLocation.latitude, 
      lng: newLocation.longitude,
      requestId: currentRequestId
    })

    try {
      programmaticMoveRef.current = true

      if (fetchNearbyEntities) {
        // Signal to skip fitBounds for the next 2 seconds to keep user's view
        map._skipFitBoundsUntil = Date.now() + 2000
        
        // fetchNearbyEntities handles setIsLoading internally, which triggers the logo spinner
        await fetchNearbyEntities(newLocation, null, false, abortControllerRef.current.signal)
      }

      // Verify this is still the latest request
      if (currentRequestId !== requestIdRef.current) {
        logger.info('Discarding stale request', { requestId: currentRequestId, current: requestIdRef.current })
        return
      }

      setTimeout(() => {
        programmaticMoveRef.current = false
      }, 500)

      if (reverseGeocode) {
        reverseGeocode(newLocation.latitude, newLocation.longitude)
          .then(result => {
            if (result && result.formattedAddress) {
              if (setSearchLocationName) {
                setSearchLocationName(result.formattedAddress)
              }
              if (setSearchLocation) {
                setSearchLocation(result.formattedAddress)
              }
              if (saveLocation) {
                saveLocation({
                  latitude: newLocation.latitude,
                  longitude: newLocation.longitude,
                  name: result.formattedAddress
                })
              }
            }
          })
          .catch(error => {
            logger.warn('Reverse geocoding failed', error)
          })
      }

    } catch (error) {
      // Don't show error for aborted requests
      if (error.name === 'AbortError') {
        logger.info('Request aborted (newer request initiated)', { requestId: currentRequestId })
        return
      }
      
      logger.error('Failed to load entities after map drag', error)
      if (t && currentRequestId === requestIdRef.current) {
        toast.error(t('map.failedToLoadNearby', 'Failed to load nearby entities'))
      }
    } finally {
      isLoadingRef.current = false
      isDraggingRef.current = false
    }
  }, [
    enabled,
    map,
    fetchNearbyEntities,
    reverseGeocode,
    setUserLocation,
    setSearchLocationName,
    setSearchLocation,
    saveLocation,
    t
  ])

  useEffect(() => {
    if (!map || !enabled) return

    const handleDragStart = () => {
      isDraggingRef.current = true
    }

    const handleDragEnd = () => {
      // Reset drag flag shortly after dragend so the immediately-following
      // moveend can still trigger the handler, but any LATER moveend (e.g.
      // from a marker click's panBy) won't see a stale isDragging=true.
      setTimeout(() => {
        isDraggingRef.current = false
      }, 1200)
    }

    const handleMoveEnd = () => {
      if (programmaticMoveRef.current) {
        programmaticMoveRef.current = false
        return
      }

      // Skip programmatic pans (marker click centering, popup auto-pan, etc.)
      if (map._programmaticMove) return
      if (map._suppressBoundsUntil && Date.now() < map._suppressBoundsUntil) return

      if (!isDraggingRef.current) return

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      debounceTimerRef.current = setTimeout(() => {
        handleMapMove()
      }, 1000)
    }

    map.on('dragstart', handleDragStart)
    map.on('dragend', handleDragEnd)
    map.on('moveend', handleMoveEnd)

    return () => {
      map.off('dragstart', handleDragStart)
      map.off('dragend', handleDragEnd)
      map.off('moveend', handleMoveEnd)
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [map, enabled, handleMapMove])

  return null
})

export default MapDragHandler
